// Must be imported BEFORE React for why-did-you-render to work
// Enable with: VITE_WDYR=true yarn oak:dev
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { Worker } from "@react-pdf-viewer/core";
import { AlertProvider } from "components/Alerts/AlertProvider";
import { BreakpointDisplay } from "components/BreakpointDisplay";
import { HasFeature } from "components/HasFeature";
import { HasFlag } from "components/HasFlag";
import { HasPermission } from "components/HasPermission";
import { IDPAuthCallback } from "components/IDPAuthCallback";
import { LocalizationProviderWrapper } from "components/LocalizationProvider";
import { ReportPayments } from "components/Payment/ReportPayments";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { Permission, SubmissionStepType } from "generated-graphql/graphql";
import { OmnisearchProvider } from "providers/OmnisearchContext";
import { ScreenWidthProvider } from "providers/screenWidth";
import { TokenProviderEnabler } from "providers/token";
import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, Navigate, Outlet, redirect, RouterProvider } from "react-router-dom";
import { ChemicalDetail } from "routes/Customer/Chemicals/Catalog/Chemical";
import { TenantChemicalsTable } from "routes/Customer/Chemicals/Catalog/TenantCatalog/TenantChemicalsTable";
import { TenantProductsTable } from "routes/Customer/Chemicals/Catalog/TenantCatalog/TenantProductsTable";
import { ChemicalFacilityDocuments } from "routes/Customer/Chemicals/ChemicalFacilityDocuments";
import { ContactsFacilitiesTable } from "routes/Customer/Chemicals/Contacts/facilities";
import { DocumentFacilitiesTable } from "routes/Customer/Chemicals/DocumentFacilitiesTable";
import { FacilitiesTable } from "routes/Customer/Chemicals/Facilities";
import { FacilityInventoryTable } from "routes/Customer/Chemicals/Inventory/facilities";
import { ChemicalSummary } from "routes/Customer/Chemicals/Inventory/Facility/ChemicalSummary";
import { FacilityChemical } from "routes/Customer/Chemicals/Inventory/Facility/FacilityChemical";
import { FacilityChemicalMeasurementDetails } from "routes/Customer/Chemicals/Inventory/Facility/FacilityChemicalMeasurementDetails";
import { FacilityProduct } from "routes/Customer/Chemicals/Inventory/Facility/FacilityProduct";
import { InventoryDefaultNavigation } from "routes/Customer/Chemicals/Inventory/Facility/InventoryDefaultNavigation";
import TierIIReport from "routes/Customer/Chemicals/Report";
import { ChemicalsReportStep } from "routes/Customer/Chemicals/Report/Chemicals";
import { Summary } from "routes/Customer/Chemicals/Report/Chemicals/Summary";
import { ReportFacilityContacts } from "routes/Customer/Chemicals/Report/Contacts/ReportFacilityContacts";
import { Documents as ReportDocuments } from "routes/Customer/Chemicals/Report/Documents/Documents";
import { Facility } from "routes/Customer/Chemicals/Report/Facility";
import { Overview } from "routes/Customer/Chemicals/Report/Overview";
import { ProductsReportStep } from "routes/Customer/Chemicals/Report/Products/index";
import { Regulatory } from "routes/Customer/Chemicals/Report/Regulatory";
import { ReviewersAndVerifier } from "routes/Customer/Chemicals/Report/Review/ReviewersAndVerifiers";
import { StateInformation } from "routes/Customer/Chemicals/Report/StateInformation";
import { Submitted } from "routes/Customer/Chemicals/Report/Submitted/Submitted";
import { VerifyAndSubmit } from "routes/Customer/Chemicals/Report/VerifyAndSubmit";
import { WhenVerified } from "routes/Customer/Chemicals/Report/WhenVerified";
import { ReportsTable } from "routes/Customer/Chemicals/Reports";
import { Credentials as TenantCredentials } from "routes/Customer/Credentials";
import { Documents as TenantDocuments } from "routes/Customer/Documents";
import { Facility as FacilityIndex } from "routes/Customer/Facility";
import { Contacts as FacilityContacts } from "routes/Customer/Facility/Contacts";
import { Documents as FacilityDocuments } from "routes/Customer/Facility/Documents";
import { FacilityProfileSwitch } from "routes/Customer/Facility/FacilityProfileSwitch";
import { FacilityTasksV2 } from "routes/Customer/Facility/FacilityTasksV2";
import { Organizations } from "routes/Customer/Organizations";
import { Scout } from "routes/Customer/Scout";
import { Tasks } from "routes/Customer/Tasks";
import { Users as TenantUsers } from "routes/Customer/Users";
import { WasteContactsFacilitiesTable } from "routes/Customer/Waste/Contacts/facilities";
import { WasteContacts } from "routes/Customer/Waste/Contacts/WasteContacts";
import { WasteFacility } from "routes/Customer/Waste/Facilities/Facility/WasteFacility";
import { WasteFacilities } from "routes/Customer/Waste/Facilities/WasteFacilities";
import { ManifestLineItems } from "routes/Customer/Waste/ManifestLineItems/ManifestLineItems";
import { Manifests } from "routes/Customer/Waste/Manifests/Manifests";
import { WasteSubmissions } from "routes/Customer/Waste/Submissions";
import { CreateEditSubmission } from "routes/Customer/Waste/Submissions/CreateEdit";
import { ReadOnlySubmission } from "routes/Customer/Waste/Submissions/ReadOnly";
import { StepperOverview } from "routes/Customer/Waste/Submissions/Stepper/StepperOverview";
import { StepRoute } from "routes/Customer/Waste/Submissions/Stepper/StepRoute";
import { WasteSubmissionStepper } from "routes/Customer/Waste/Submissions/Stepper/WasteSubmissionStepper";
import { TenantWasteStreams } from "routes/Customer/Waste/WasteStreams/TenantWasteStreams";
import { NotFound } from "routes/NotFound";
import { Agencies } from "routes/Staff/Agencies";
import { FireDepartments } from "routes/Staff/Agencies/FireDepartments";
import { Lepcs } from "routes/Staff/Agencies/Lepcs";
import { NJLeadAgencies } from "routes/Staff/Agencies/NJLeadAgencies";
import { NJPoliceDepartments } from "routes/Staff/Agencies/NJPoliceDepartments";
import { Sercs } from "routes/Staff/Agencies/Sercs";
import { AmazonWadm } from "routes/Staff/AmazonWadm";
import { CohortDetails } from "routes/Staff/Cohorts/CohortDetails";
import { CohortJobs } from "routes/Staff/Cohorts/Jobs";
import { Payments } from "routes/Staff/Cohorts/Payments";
import { CredentialsDashboard } from "routes/Staff/Credentials";
import { EpcraCredentialManagement } from "routes/Staff/Credentials/EpcraCredentialManagement";
import { RcraInfoCredentials } from "routes/Staff/Credentials/RcraInfoCredentials";
import { SteersCredentials } from "routes/Staff/Credentials/SteersCredentials";
import { ErrorClustering } from "routes/Staff/ErrorClustering";
import { StaffFeatureFlagsPage } from "routes/Staff/FeatureFlags";
import { AssignmentManagementTableV2 } from "routes/Staff/Fulfillment/AssignmentManagerTable/index";
import { Activities } from "routes/Staff/Fulfillment/FulfillmentDetails/Activities";
import { ActivityDetails } from "routes/Staff/Fulfillment/FulfillmentDetails/Activities/ActivityDetails";
import { Documents as StaffDocuments } from "routes/Staff/Fulfillment/FulfillmentDetails/Documents";
import { WebAutomationJobInfo } from "routes/Staff/Fulfillment/FulfillmentDetails/WebAutomationJobInfo";
import { WorkflowLogs } from "routes/Staff/Fulfillment/FulfillmentDetails/WorkflowLogs";
import { ActivityLogs } from "routes/Staff/Fulfillment/FulfillmentDetails/WorkflowLogs/ActivityLogs";
import { FulfillmentDashboard } from "routes/Staff/Fulfillment/index";
import { WaitForAllReport } from "routes/Staff/Fulfillment/WaitForAllReport";
import { WorkflowActivitiesTable } from "routes/Staff/Fulfillment/WorkflowActivitiesTable";
import { GeneratorStatusChanges } from "routes/Staff/GeneratorStatusChanges";
import { GlobalPayments } from "routes/Staff/GlobalPayments";
import { GotoIdRoute } from "routes/Staff/GotoIdRoute";
import { Jobs } from "routes/Staff/Jobs";
import { Notifications } from "routes/Staff/Notifications";
import { Observe } from "routes/Staff/Observe";
import { ScoutConversationViewerRoute } from "routes/Staff/Observe/ScoutConversationViewerRoute";
import { StaffScoutControllerProvider } from "routes/Staff/Observe/StaffScoutControllerProvider";
import { PipelinesTabs } from "routes/Staff/Pipelines";
import { PipelineRunResults } from "routes/Staff/Pipelines/PipelineRunResults";
import { Requirements } from "routes/Staff/Requirements";
import { Roles } from "routes/Staff/Roles";
import { SubscriptionOverview } from "routes/Staff/SubscriptionOverview";
import { Tags } from "routes/Staff/Tags/Tags";
import { WasteFulfillmentDashboard } from "routes/Staff/WasteFulfillment";
import { Portals } from "routes/Staff/WorkflowConfig/Portals";
import configureSentry from "sentry";

import { App } from "./App";
import "./polyfills/global";
import configureMuiInstallation from "./mui-config";
import { ApolloProvider } from "./providers/apollo";
import { AuthProvider } from "./providers/auth";
import { ThemeModeProvider } from "./providers/themeMode";
import { CurrentUserProvider } from "./providers/user";
import { LandingPage } from "./routes";
import { Customer } from "./routes/Customer";
import { Chemicals as ChemicalsIndex } from "./routes/Customer/Chemicals";
import { FacilityInventoryDetail } from "./routes/Customer/Chemicals/Inventory/Facility";
import { Overview as ChemicalsOverview } from "./routes/Customer/Chemicals/Overview/index";
import { Contacts as TenantContacts } from "./routes/Customer/Contacts";
import { EchoProfile } from "./routes/Customer/Facility/EchoProfile";
import { NpdesProfile } from "./routes/Customer/Facility/NpdesProfile";
import { RcraProfile } from "./routes/Customer/Facility/RcraProfile";
import { Insights as InsightsIndex } from "./routes/Customer/Insights";
import { Benchmark as InsightsBenchmark } from "./routes/Customer/Insights/Benchmark";
import { Echo as InsightsEcho } from "./routes/Customer/Insights/Echo";
import { EchoDetail } from "./routes/Customer/Insights/Echo/EchoDetail";
import { Npdes as InsightsNpdes } from "./routes/Customer/Insights/Npdes/Npdes";
import { NpdesDetail } from "./routes/Customer/Insights/Npdes/NpdesDetail";
import { Overview as InsightsOverview } from "./routes/Customer/Insights/Overview";
import { RcraInfo as InsightsRcraInfo } from "./routes/Customer/Insights/RcraInfo/RcraInfo";
import { RcraInfoDetail } from "./routes/Customer/Insights/RcraInfo/RcraInfoDetail";
import { Waste as WasteIndex } from "./routes/Customer/Waste";
import { WasteOverview } from "./routes/Customer/Waste/Overview";
import { Oops } from "./routes/Oops";
import { PrivateRoute } from "./routes/PrivateRoute";
import { QRCodeGenerator } from "./routes/QRCodeGenerator";
import { Staff } from "./routes/Staff";
import { Activities as CohortActivities } from "./routes/Staff/Cohorts/Activities";
import { ActivityLogs as CohortActivityLogs } from "./routes/Staff/Cohorts/Activities/ActivityLogs";
import { CohortActivityDetails } from "./routes/Staff/Cohorts/Activities/CohortActivityDetails";
import { WorkflowLogs as CohortWorkflowLogs } from "./routes/Staff/Cohorts/Activities/WorkflowLogs";
import { CohortDocuments } from "./routes/Staff/Cohorts/Documents";
import { DemoOrganizationCreator } from "./routes/Staff/DemoOrganizationCreator";
import { ReportDetails } from "./routes/Staff/Fulfillment/FulfillmentDetails";
import { Implementation } from "./routes/Staff/Implementation";
import { ImplementationTabs } from "./routes/Staff/Implementation/OrganizationDetails";
import { Communications } from "./routes/Staff/MailRoom/Communications";
import { CommunicationDetails } from "./routes/Staff/MailRoom/Communications/CommunicationDetails";
import { CommunicationsTable } from "./routes/Staff/MailRoom/Communications/CommunicationsTable";
import { CreateCommunications } from "./routes/Staff/MailRoom/Communications/CreateCommunications";
import { Sse } from "./routes/Staff/Sse";
import { SubmissionErrorsDashboard } from "./routes/Staff/SubmissionErrors";
import { ManifestReviewPage } from "./routes/Staff/WasteFulfillment/ManifestReview/ManifestReviewPage";
import { WasteSubmissionLandingPage } from "./routes/Staff/WasteFulfillment/WasteSubmissionLandingPage";
import { WorkflowConfig } from "./routes/Staff/WorkflowConfig";
import { DeniedFacilities } from "./routes/Staff/WorkflowConfig/DeniedFacilities";
import { FacilityPilotProgram } from "./routes/Staff/WorkflowConfig/FacilityPilotProgram";
import { Rules } from "./routes/Staff/WorkflowConfig/Rules";
import "./wdyr";

// Initialize Apollo client dev helpers in development mode
if (import.meta.env.DEV) {
  import("@apollo/client/dev").then(({ loadErrorMessages, loadDevMessages }) => {
    loadErrorMessages();
    loadDevMessages();
  });
}

// Set up Sentry integration
configureSentry();

// Set up the MUI theme and license key
configureMuiInstallation();

const router = createBrowserRouter([
  {
    path: "/auth/callback",
    element: (
      <Authenticator.Provider>
        <IDPAuthCallback />
      </Authenticator.Provider>
    ),
    errorElement: <Oops />,
  },

  {
    path: "/reset-password",
    element: (
      <AuthProvider>
        <Navigate to="/" replace />
      </AuthProvider>
    ),
    errorElement: <Oops />,
  },

  {
    element: (
      <AuthProvider>
        <CurrentUserProvider>
          <PrivateRoute />
        </CurrentUserProvider>
      </AuthProvider>
    ),
    errorElement: <Oops />,
    children: [
      {
        path: "/",
        element: <App />,
        children: [
          {
            errorElement: <Oops />,
            path: "staff",
            element: <Staff />,
            children: [
              {
                index: true,
                element: <LandingPage />,
              },
              {
                path: "id/:id",
                element: <GotoIdRoute />,
              },
              {
                path: "sse/:tenantId",
                element: (
                  <HasFlag flag={FeatureFlagKey.Sse} fallback="..">
                    <Sse />
                  </HasFlag>
                ),
              },
              {
                path: "observe",
                element: <Observe />,
              },
              {
                path: "observe/scout-conversation/:conversationId",
                element: (
                  <StaffScoutControllerProvider>
                    <ScoutConversationViewerRoute />
                  </StaffScoutControllerProvider>
                ),
              },
              {
                path: "roles",
                element: <Roles />,
              },
              {
                path: "fulfillment",
                element: <FulfillmentDashboard />,
                children: [
                  {
                    index: true,
                    element: <Navigate to={`facilities${window.location.search}`} replace />,
                  },
                  {
                    path: "facilities",
                    element: <AssignmentManagementTableV2 />,
                  },
                  {
                    path: "workflows",
                    element: <WorkflowActivitiesTable />,
                  },
                  {
                    path: "wait-for-all",
                    element: <WaitForAllReport />,
                  },
                  {
                    path: "error-clustering",
                    element: <ErrorClustering />,
                  },
                  {
                    path: "waste",
                    element: <WasteFulfillmentDashboard />,
                    children: [
                      {
                        path: "submissions/:submissionId",
                        element: <WasteSubmissionLandingPage />,
                      },
                      {
                        path: "manifest-review",
                        element: <ManifestReviewPage />,
                      },
                    ],
                  },
                  {
                    path: "f/:facilityId",
                    loader: ({ params }) => {
                      return redirect(
                        `/staff/fulfillment/f/${params.facilityId}/${currentTierIIReportingYear}`
                      );
                    },
                  },
                  {
                    path: ":reportId",
                    loader: ({ params }) => redirect(`/staff/fulfillment/r/${params.reportId}`),
                  },
                  {
                    path: ":reportId/*",
                    loader: ({ params }) => {
                      const remainingPath = params["*"];
                      return redirect(
                        `/staff/fulfillment/r/${params.reportId}/${remainingPath || ""}`
                      );
                    },
                  },
                  {
                    path: "r/:reportId",
                    element: <ReportDetails />,
                    children: [
                      {
                        path: "activities",
                        element: <Activities />,
                        children: [
                          {
                            path: ":activityId",
                            element: <ActivityDetails />,
                          },
                        ],
                      },
                      {
                        path: "documents",
                        element: <StaffDocuments />,
                      },
                      {
                        path: "payments",
                        children: [
                          {
                            index: true,
                            element: <ReportPayments />,
                          },
                          {
                            path: ":paymentId",
                            element: <ReportPayments />,
                          },
                        ],
                      },

                      {
                        path: "workflow-logs",
                        element: <WorkflowLogs />,
                        children: [
                          {
                            path: ":activityId",
                            element: <ActivityLogs />,
                          },
                        ],
                      },
                      {
                        path: "job",
                        element: <WebAutomationJobInfo />,
                      },
                    ],
                  },
                  {
                    path: "f/:facilityId/:reportingYear",
                    element: <ReportDetails />,
                    children: [
                      {
                        path: "activities",
                        element: <Activities />,
                        children: [
                          {
                            path: ":activityId",
                            element: <ActivityDetails />,
                          },
                        ],
                      },
                      {
                        path: "documents",
                        element: <StaffDocuments />,
                      },
                      {
                        path: "payments",
                        children: [
                          {
                            index: true,
                            element: <ReportPayments />,
                          },
                          {
                            path: ":paymentId",
                            element: <ReportPayments />,
                          },
                        ],
                      },
                      {
                        path: "workflow-logs",
                        element: <WorkflowLogs />,
                        children: [
                          {
                            path: ":activityId",
                            element: <ActivityLogs />,
                          },
                        ],
                      },
                      {
                        path: "job",
                        element: <WebAutomationJobInfo />,
                      },
                    ],
                  },
                ],
              },
              {
                path: "requirements",
                element: (
                  <OmnisearchProvider>
                    <Requirements />
                  </OmnisearchProvider>
                ),
              },
              {
                path: "implementation",
                element: <Implementation />,
                children: [
                  {
                    path: ":tenantId",
                    element: <ImplementationTabs />,
                  },
                ],
              },
              {
                path: "agencies",
                element: <Agencies />,
                children: [
                  {
                    path: "lepcs/:lepcId?",
                    element: <Lepcs />,
                  },
                  {
                    path: "fireDepartments/:fireDepartmentId?",
                    element: <FireDepartments />,
                  },
                  {
                    path: "njPoliceDepartments",
                    element: <NJPoliceDepartments />,
                  },
                  {
                    path: "njLeadAgencies",
                    element: <NJLeadAgencies />,
                  },
                  {
                    path: "sercs/:sercAgencyId?",
                    element: <Sercs />,
                  },
                ],
              },
              {
                path: "submission-errors",
                element: <SubmissionErrorsDashboard />,
                children: [
                  {
                    path: ":reportId",
                    element: <ReportDetails />,
                  },
                ],
              },
              {
                path: "mail-room",
                element: <Communications />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="status" replace />,
                  },
                  {
                    path: "status",
                    element: <CommunicationsTable />,
                  },
                  {
                    path: "send",
                    element: <CreateCommunications />,
                  },
                  {
                    path: "status/:communicationId",
                    element: <CommunicationDetails />,
                  },
                ],
              },
              {
                path: "workflow-config",
                element: <WorkflowConfig />,
                children: [
                  {
                    path: "rules",
                    element: <Rules />,
                  },
                  {
                    path: "denied-facilities",
                    element: <DeniedFacilities />,
                  },
                  {
                    path: "pilot-program",
                    children: [
                      {
                        index: true,
                        element: <Navigate to="states" replace />,
                      },
                      {
                        path: "states",
                        element: <FacilityPilotProgram />,
                      },
                      {
                        path: "local",
                        element: <FacilityPilotProgram />,
                      },
                    ],
                  },
                  {
                    path: "portals/:portalId?",
                    element: (
                      <OmnisearchProvider>
                        <Portals />
                      </OmnisearchProvider>
                    ),
                  },
                ],
              },
              {
                path: "notifications",
                element: <Notifications />,
              },
              {
                path: "generator-status-changes",
                element: <GeneratorStatusChanges />,
              },
              {
                path: "subscription-overview",
                element: <SubscriptionOverview />,
              },
              {
                path: "payments",
                children: [
                  {
                    index: true,
                    element: <GlobalPayments />,
                  },
                  {
                    path: ":paymentId",
                    element: <GlobalPayments />,
                  },
                ],
              },
              {
                path: "jobs/:jobId?",
                element: <Jobs />,
              },
              {
                path: "pipelines",
                element: <PipelinesTabs />,
              },
              {
                path: "pipelines/:pipelineId/results",
                element: <PipelineRunResults />,
              },
              {
                path: "amazon-wadm",
                element: <AmazonWadm />,
              },
              {
                path: "insights-demo-org-creator",
                element: <DemoOrganizationCreator />,
              },
              {
                path: "credentials",
                element: <CredentialsDashboard />,
                children: [
                  {
                    path: "epcra/:credentialImplementationId?",
                    element: <EpcraCredentialManagement />,
                  },
                  {
                    path: "rcra-info",
                    element: <RcraInfoCredentials />,
                  },
                  {
                    path: "steers",
                    element: <SteersCredentials />,
                  },
                ],
              },
              {
                path: "cohorts/:cohortId",
                element: <CohortDetails />,
                children: [
                  {
                    path: "activities",
                    element: <CohortActivities />,
                    children: [
                      {
                        path: ":activityId",
                        element: <CohortActivityDetails />,
                      },
                    ],
                  },
                  { path: "payments/:paymentId?", element: <Payments /> },
                  {
                    path: "documents",
                    element: <CohortDocuments />,
                  },
                  {
                    path: "workflow-logs",
                    element: <CohortWorkflowLogs />,
                    children: [
                      {
                        path: ":activityId",
                        element: <CohortActivityLogs />,
                      },
                    ],
                  },
                  {
                    path: "jobs",
                    element: <CohortJobs />,
                  },
                ],
              },
              {
                path: "tags",
                element: <Tags />,
              },
              {
                path: "feature-flags",
                element: <StaffFeatureFlagsPage />,
              },
            ],
          },
          {
            path: "o/:tenantId",
            element: <Customer />,
            errorElement: <Oops />,
            children: [
              {
                index: true,
                element: <LandingPage />,
              },
              {
                path: "organizations",
                element: <Organizations />,
              },
              {
                path: "facilities",
                element: (
                  <HasPermission permissions={[Permission.RouteFacilities]}>
                    <FacilitiesTable />
                  </HasPermission>
                ),
              },
              {
                path: "insights",
                element: (
                  <HasPermission permissions={[Permission.RouteInsights]}>
                    <InsightsIndex />
                  </HasPermission>
                ),
                children: [
                  {
                    path: "overview",
                    element: <InsightsOverview />,
                  },
                  {
                    path: "benchmark",
                    element: <InsightsBenchmark />,
                  },
                  {
                    path: "echo",
                    element: <InsightsEcho />,
                  },
                  {
                    path: "rcra-info",
                    element: <InsightsRcraInfo />,
                  },
                  {
                    path: "npdes",
                    element: <InsightsNpdes />,
                  },
                  {
                    path: "rcra-info/:handlerId",
                    element: <RcraInfoDetail />,
                  },
                  {
                    path: "echo/:registryId",
                    element: <EchoDetail />,
                  },
                  {
                    path: "npdes/:npdesId",
                    element: <NpdesDetail />,
                  },
                ],
              },
              {
                path: "facilities/:facilityId",
                element: <FacilityIndex />,
                children: [
                  {
                    path: "profile",
                    element: <FacilityProfileSwitch />,
                  },
                  {
                    path: "tasks/:virtualEventId?",
                    element: <FacilityTasksV2 />,
                  },
                  {
                    path: "echoInsights",
                    element: <EchoProfile />,
                  },
                  {
                    path: "rcraInsights",
                    element: <RcraProfile />,
                  },
                  {
                    path: "npdesInsights",
                    element: <NpdesProfile />,
                  },
                  {
                    path: "contacts",
                    element: (
                      <HasPermission permissions={[Permission.RouteContacts]}>
                        <FacilityContacts />
                      </HasPermission>
                    ),
                  },
                  {
                    path: "documents",
                    element: (
                      <HasPermission permissions={[Permission.RouteDocuments]}>
                        <FacilityDocuments />
                      </HasPermission>
                    ),
                  },
                  {
                    path: "credentials/:credentialId?",
                    element: (
                      <HasPermission permissions={[Permission.RouteEpcraCredentials]}>
                        <TenantCredentials />
                      </HasPermission>
                    ),
                  },
                ],
              },
              {
                path: "chemicals",
                element: <ChemicalsIndex />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="overview" replace />,
                  },
                  {
                    path: "overview",
                    element: <ChemicalsOverview />,
                  },
                  {
                    path: "reports",
                    element: (
                      <HasPermission permissions={[Permission.RouteEpcraReports]}>
                        <Outlet />
                      </HasPermission>
                    ),
                    children: [
                      {
                        index: true,
                        element: (
                          <OmnisearchProvider>
                            <ReportsTable />
                          </OmnisearchProvider>
                        ),
                      },
                      {
                        path: ":reportId/*",
                        element: <TierIIReport />,
                        children: [
                          {
                            index: true,
                            element: (
                              <WhenVerified fallback={<Overview />}>
                                <Submitted />
                              </WhenVerified>
                            ),
                          },
                          {
                            path: "facility",
                            element: <Facility />,
                          },
                          {
                            path: "regulatory",
                            element: <Regulatory />,
                          },
                          {
                            path: "chemicals/:facilityChemicalId?",
                            element: (
                              <HasFeature not products fallback="../products">
                                <ChemicalsReportStep />
                              </HasFeature>
                            ),
                          },
                          {
                            path: "chemicals/measurements/:chemicalId",
                            element: (
                              <HasFeature not products fallback="../chemicals">
                                <FacilityChemicalMeasurementDetails />
                              </HasFeature>
                            ),
                          },
                          {
                            path: "products/:productId?",
                            element: (
                              <HasFeature products fallback="../chemicals">
                                <ProductsReportStep />
                              </HasFeature>
                            ),
                          },
                          {
                            path: "other-chemicals/:facilityChemicalId?",
                            element: (
                              <HasFeature products fallback="../chemicals">
                                <ChemicalsReportStep />
                              </HasFeature>
                            ),
                          },
                          {
                            path: "other-chemicals/measurements/:chemicalId",
                            element: (
                              <HasFeature products fallback="../chemicals">
                                <FacilityChemicalMeasurementDetails />
                              </HasFeature>
                            ),
                          },
                          {
                            path: "chemical-summary",
                            element: <Summary />,
                          },
                          {
                            path: "contacts",
                            element: <ReportFacilityContacts />,
                          },
                          {
                            path: "documents",
                            element: <ReportDocuments />,
                          },
                          {
                            path: "state-information",
                            element: <StateInformation />,
                          },
                          {
                            path: "review",
                            element: (
                              <WhenVerified fallback={<Overview />}>
                                <ReviewersAndVerifier />
                              </WhenVerified>
                            ),
                          },
                          {
                            path: "verify-submit",
                            element: (
                              <WhenVerified fallback={<VerifyAndSubmit />}>
                                <Navigate to="../" />
                              </WhenVerified>
                            ),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "catalog",
                    element: (
                      <HasPermission permissions={[Permission.RouteEpcraProducts]}>
                        <Outlet />
                      </HasPermission>
                    ),
                    children: [
                      {
                        index: true,
                        element: <Navigate to="chemicals" />,
                      },
                      {
                        path: "chemicals",
                        element: <TenantChemicalsTable />,
                      },
                      {
                        path: "chemicals/:chemicalId",
                        element: <ChemicalDetail />,
                      },
                    ],
                  },
                  {
                    path: "inventory",
                    children: [
                      {
                        index: true,
                        element: <FacilityInventoryTable />,
                      },
                      {
                        path: ":facilityId",
                        element: <FacilityInventoryDetail />,
                        children: [
                          {
                            index: true,
                            element: <InventoryDefaultNavigation />,
                          },
                          {
                            path: "chemicals/:facilityChemicalId?",
                            children: [
                              {
                                index: true,
                                element: (
                                  <HasFeature not products fallback="../products">
                                    <FacilityChemical />
                                  </HasFeature>
                                ),
                              },
                              {
                                path: "measurements/:chemicalId",
                                element: <FacilityChemicalMeasurementDetails />,
                              },
                            ],
                          },
                          {
                            path: "products/:productId?",
                            element: (
                              <HasFeature products fallback="../chemicals">
                                <FacilityProduct />
                              </HasFeature>
                            ),
                          },
                          {
                            path: "other-chemicals/:facilityChemicalId?",
                            element: (
                              <HasFeature products fallback="../chemicals">
                                <FacilityChemical />
                              </HasFeature>
                            ),
                          },
                          {
                            path: "other-chemicals/measurements/:chemicalId",
                            element: (
                              <HasFeature products fallback="../chemicals">
                                <FacilityChemicalMeasurementDetails />
                              </HasFeature>
                            ),
                          },
                          {
                            path: "chemical-summary",
                            element: <ChemicalSummary />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "contacts",
                    children: [
                      {
                        index: true,
                        element: (
                          <HasPermission permissions={[Permission.RouteContacts]}>
                            <ContactsFacilitiesTable />
                          </HasPermission>
                        ),
                      },
                      {
                        path: ":facilityId",
                        element: (
                          <HasPermission permissions={[Permission.RouteContacts]}>
                            <FacilityContacts />
                          </HasPermission>
                        ),
                      },
                    ],
                  },
                  {
                    path: "documents",
                    children: [
                      {
                        index: true,
                        element: (
                          <HasPermission permissions={[Permission.RouteDocuments]}>
                            <DocumentFacilitiesTable />
                          </HasPermission>
                        ),
                      },
                      {
                        path: ":facilityId",
                        element: (
                          <HasPermission permissions={[Permission.RouteDocuments]}>
                            <ChemicalFacilityDocuments />
                          </HasPermission>
                        ),
                      },
                    ],
                  },
                  {
                    path: "products/:productId?",
                    children: [
                      {
                        index: true,
                        element: (
                          <HasPermission permissions={[Permission.RouteEpcraProducts]}>
                            <HasFeature products fallback="../catalog/chemicals">
                              <TenantProductsTable />
                            </HasFeature>
                          </HasPermission>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: "waste",
                element: <WasteIndex />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="overview" replace />,
                  },
                  {
                    path: "overview",
                    element: <WasteOverview />,
                  },
                  {
                    path: "facilities",
                    element: <WasteFacilities />,
                  },
                  {
                    path: "facilities/:facilityId",
                    element: <WasteFacility />,
                  },
                  {
                    path: "facilities/:facilityId/wasteStream/:wasteStreamId?",
                    element: <WasteFacility />,
                  },
                  {
                    path: "facilities/:facilityId/manifest-line-items/:wasteLineUnifiedId?",
                    element: <WasteFacility />,
                  },
                  {
                    path: "streams/:wasteStreamId?/:wasteLineUnifiedId?",
                    element: (
                      <HasFlag flag={FeatureFlagKey.WasteStreamsTenantPage} fallback="../">
                        <TenantWasteStreams />
                      </HasFlag>
                    ),
                  },
                  {
                    path: "manifests/:processedManifestId?/:wasteLineUnifiedId?",
                    element: <Manifests />,
                  },
                  {
                    path: "manifest-line-items/:wasteLineUnifiedId?",
                    element: <ManifestLineItems />,
                  },
                  {
                    path: "contacts",
                    children: [
                      {
                        index: true,
                        element: (
                          <HasFlag
                            flag={FeatureFlagKey.RcraFacilityProfile}
                            fallback="../facilities"
                          >
                            <HasPermission permissions={[Permission.RouteContacts]}>
                              <WasteContactsFacilitiesTable />
                            </HasPermission>
                          </HasFlag>
                        ),
                      },
                      {
                        path: ":facilityId",
                        element: (
                          <HasFlag
                            flag={FeatureFlagKey.RcraFacilityProfile}
                            fallback="../facilities"
                          >
                            <HasPermission permissions={[Permission.RouteContacts]}>
                              <WasteContacts />
                            </HasPermission>
                          </HasFlag>
                        ),
                      },
                    ],
                  },
                  {
                    path: "submissions",
                    children: [
                      {
                        index: true,
                        element: (
                          <HasFlag flag={FeatureFlagKey.WasteSubmissions} fallback="../facilities">
                            <WasteSubmissions />
                          </HasFlag>
                        ),
                      },
                      {
                        path: "create",
                        element: (
                          <HasFlag flag={FeatureFlagKey.WasteSubmissions} fallback="../">
                            <CreateEditSubmission />
                          </HasFlag>
                        ),
                      },
                      {
                        path: ":submissionId",
                        element: (
                          <HasFlag flag={FeatureFlagKey.WasteSubmissions} fallback="../">
                            <ReadOnlySubmission />
                          </HasFlag>
                        ),
                      },
                      {
                        path: ":submissionId/edit",
                        element: (
                          <HasFlag flag={FeatureFlagKey.WasteSubmissions} fallback="../">
                            <CreateEditSubmission />
                          </HasFlag>
                        ),
                      },
                      {
                        path: ":submissionId/stepper/*",
                        element: (
                          <HasFlag flag={FeatureFlagKey.WasteSubmissionStepper} fallback="../">
                            <WasteSubmissionStepper />
                          </HasFlag>
                        ),
                        children: [
                          { index: true, element: <StepperOverview /> },
                          {
                            // Details moved into the Overview; keep the old path
                            // working by redirecting to the overview.
                            path: "details",
                            element: <Navigate to=".." replace />,
                          },
                          {
                            path: "facility-profile",
                            element: <StepRoute step={SubmissionStepType.FacilityProfile} />,
                          },
                          {
                            path: "waste-activity",
                            element: <StepRoute step={SubmissionStepType.RcraWasteActivity} />,
                          },
                          {
                            // Supports the waste-stream slide-over opened from the
                            // Waste Activity tile without leaving the stepper — the
                            // `:wasteStreamId` param drives the same inline drawer the
                            // waste facility page uses.
                            path: "waste-activity/wasteStream/:wasteStreamId",
                            element: <StepRoute step={SubmissionStepType.RcraWasteActivity} />,
                          },
                          {
                            path: "contacts",
                            element: <StepRoute step={SubmissionStepType.Contacts} />,
                          },
                          {
                            path: "state-info",
                            element: <StepRoute step={SubmissionStepType.StateFields} />,
                          },
                          {
                            path: "review",
                            element: <StepRoute step={SubmissionStepType.Review} />,
                          },
                          {
                            path: "verify-and-submit",
                            element: <StepRoute step={SubmissionStepType.VerifyAndSubmit} />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: "contacts",
                element: (
                  <HasPermission permissions={[Permission.RouteContacts]}>
                    <TenantContacts />
                  </HasPermission>
                ),
              },
              {
                path: "documents/:documentId?",
                element: (
                  <HasPermission permissions={[Permission.RouteDocuments]}>
                    <TenantDocuments />
                  </HasPermission>
                ),
              },
              {
                path: "credentials/:credentialId?",
                element: (
                  <HasPermission permissions={[Permission.RouteEpcraCredentials]}>
                    <TenantCredentials />
                  </HasPermission>
                ),
              },
              {
                path: "scout/:conversationId?",
                element: (
                  <HasFlag flag={FeatureFlagKey.Scout} fallback="../">
                    <HasPermission permissions={[Permission.RouteScout]}>
                      <Scout />
                    </HasPermission>
                  </HasFlag>
                ),
              },
              {
                path: "tasks/:virtualEventId?",
                element: (
                  <HasFeature tasks fallback="..">
                    <Tasks />
                  </HasFeature>
                ),
              },
              {
                path: "account/users",
                element: (
                  <HasPermission permissions={[Permission.WriteAllUserTenant]}>
                    <TenantUsers />
                  </HasPermission>
                ),
              },
            ],
          },
          {
            path: "home", // Legacy path commonly used as a bookmark
            loader: async () => redirect("/"),
          },
          {
            path: "qr-code",
            element: <QRCodeGenerator />,
          },
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },
    ],
  },
]);

const cognitoError = new URLSearchParams(location.search).get("error_description");
if (cognitoError) {
  localStorage.setItem("error_description", cognitoError);
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <Authenticator.Provider>
      <AlertProvider>
        <TokenProviderEnabler>
          <ApolloProvider>
            <ThemeModeProvider>
              <ScreenWidthProvider>
                <LocalizationProviderWrapper>
                  <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js">
                    <RouterProvider router={router} />
                    {import.meta.env.VITE_ENV_SHOW_BREAKPOINT === "true" && (
                      <BreakpointDisplay position={import.meta.env.VITE_ENV_BREAKPOINT_POSITION} />
                    )}
                  </Worker>
                </LocalizationProviderWrapper>
              </ScreenWidthProvider>
            </ThemeModeProvider>
          </ApolloProvider>
        </TokenProviderEnabler>
      </AlertProvider>
    </Authenticator.Provider>
  </React.StrictMode>
);
